/**
 * Shared overlay chrome for kiosk popups (options picker, salad builder).
 *
 * Fixed backdrop + centered white card with an HMH-blue title bar, a
 * scrollable body, and a fixed footer. Sits at z-40 so the inactivity
 * timeout warning (z-50) always renders above it. Backdrop tap and the
 * title-bar × both dismiss; selection state lives in the body component
 * and dies with it — nothing touches the cart until the footer commits.
 */

import { ReactNode, useEffect } from 'react';

interface KioskOverlayProps {
  title: string;
  headerBadge?: string;
  onDismiss: () => void;
  children: ReactNode;
  footer: ReactNode;
  maxWidth?: 'max-w-4xl' | 'max-w-5xl';
}

export function KioskOverlay({ title, headerBadge, onDismiss, children, footer, maxWidth = 'max-w-4xl' }: KioskOverlayProps) {
  // Escape-to-dismiss for dev/testing; the kiosk itself has no keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-6 animate-fade-in"
      onClick={onDismiss}
    >
      <div
        className={`w-full ${maxWidth} max-h-[85vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-bounce-in`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title bar */}
        <div className="bg-[#10348e] text-white pl-8 pr-3 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            <span className="text-2xl font-medium truncate">{title}</span>
            {headerBadge && (
              <span className="bg-white/20 text-white text-lg font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                {headerBadge}
              </span>
            )}
          </div>
          <button
            onClick={onDismiss}
            className="w-[72px] h-[72px] shrink-0 flex items-center justify-center text-4xl font-light
                       rounded-2xl hover:bg-white/10 active:bg-white/20 active:scale-[0.98] transition-all"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain p-8"
          style={{ touchAction: 'pan-y' }}
        >
          {children}
        </div>

        {/* Fixed footer */}
        <div className="p-6 pt-2 shrink-0">{footer}</div>
      </div>
    </div>
  );
}
